import { unwrap } from "@sagot/api-client";
import type { TicketPriority } from "@sagot/constants";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { useAfterSalesAccess } from "../assets/use-after-sales-access";
import { serviceKeys } from "../service-keys";
import { useServiceMutation, useServiceQuery } from "../use-service-query";
import { askableFields, type FormField } from "./new-ticket-form";
import { CONTACT_SEARCH_LIMIT } from "./ticket-constants";
import type { TicketCreate } from "./ticket-types";

const FIELDS_STALE_MS = 5 * 60_000;

/** The custom fields a ticket of this type asks for, joined with their definitions (options, limits). */
export function useTypeFields(typeId: string): { fields: FormField[]; isPending: boolean; isError: boolean } {
  const ws = useWorkspaceId();
  const equipment = useAfterSalesAccess("assets");
  const enabled = typeId !== "";
  const placed = useServiceQuery({
    queryKey: serviceKeys.settingsTab(ws, `type-fields:${typeId}`),
    enabled,
    staleTime: FIELDS_STALE_MS,
    request: (api, signal) => api.GET("/v1/ticket-fields", { params: { query: { ticket_type_id: typeId } }, signal }),
  });
  const definitions = useServiceQuery({
    queryKey: serviceKeys.settingsTab(ws, "field-definitions:ticket"),
    enabled,
    staleTime: FIELDS_STALE_MS,
    request: (api, signal) => api.GET("/v1/ticket-field-definitions", { params: { query: { object: "ticket" } }, signal }),
  });
  const fields = placed.data && definitions.data ? askableFields(placed.data, definitions.data, equipment) : [];
  return { fields, isPending: enabled && (placed.isPending || definitions.isPending), isError: placed.isError || definitions.isError };
}

/** Contacts matching what was typed (name, e-mail), for the requester picker. */
export function useContactSearch(q: string) {
  const ws = useWorkspaceId();
  const term = q.trim();
  return useServiceQuery({
    queryKey: serviceKeys.contactList(ws, { q: term, picker: true }),
    enabled: term !== "",
    staleTime: 30_000,
    request: (api, signal) => api.GET("/v1/contacts", { params: { query: { q: term, limit: CONTACT_SEARCH_LIMIT } }, signal }),
  });
}

export function useCreateTicket() {
  return useServiceMutation({
    request: (api, body: TicketCreate) => api.POST("/v1/tickets", { body }),
    invalidate: (ws) => [serviceKeys.tickets(ws), serviceKeys.organisations(ws), serviceKeys.contacts(ws)],
  });
}

export interface RoutingQuestion {
  typeId: string;
  priority: TicketPriority;
  organisationId: string | null;
  title: string;
  description: string;
}

/** What the routing rules would do with this ticket (`POST /v1/routing-rules/test`); only for members who hold `tickets.configure`. */
export function useRoutingPreview(question: RoutingQuestion, enabled: boolean) {
  const api = useApi();
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: serviceKeys.settingsTab(ws, `routing-preview:${JSON.stringify(question)}`),
    enabled: enabled && question.typeId !== "",
    staleTime: 30_000,
    retry: false,
    queryFn: () =>
      unwrap(
        api.POST("/v1/routing-rules/test", {
          body: {
            sample: {
              type_id: question.typeId,
              priority: question.priority,
              organisation_id: question.organisationId,
              title: question.title,
              description: question.description,
              origin: "staff",
              channel: "console",
              language: "en",
              requester_verified: true,
            },
          },
        }),
      ),
  });
}
